import axios from 'axios';
import { getIdToken } from './auth';
import { isDevelopment, shouldSkipApiCalls, getMockApiResponse } from './devMode';
import { mockExtractBusinessInfo, mockTrainAIModel, mockTestAIModel } from './mockApiService';

const API_BASE_URL = '/api';

// Create an axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests with retry
api.interceptors.request.use(async (config) => {
  try {
    console.log('Getting auth token for API request...');
    // Try up to 3 times to get a valid token
    let token = null;
    let attempts = 0;
    
    while (!token && attempts < 3) {
      attempts++;
      token = await getIdToken(true); // Force refresh on each attempt
      
      if (!token) {
        console.warn(`No auth token available (attempt ${attempts}/3)`);
        // Wait a bit before trying again
        if (attempts < 3) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
    }
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('Auth token added to request');
    } else {
      console.error('Failed to get auth token after multiple attempts');
    }
  } catch (error) {
    console.error('Error getting auth token:', error);
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Log responses for debugging
api.interceptors.response.use(
  response => {
    console.log(`API Response [${response.config.url}]:`, response.status);
    return response;
  },
  error => {
    if (error.response) {
      console.error(`API Error [${error.config?.url}]:`, error.response.status, error.response.data);
    } else {
      console.error('API Error:', error.message);
    }
    return Promise.reject(error);
  }
);

// Utility function to retry failed requests
export const retryRequest = async (fn, retries = 3, delay = 1000) => {
  try {
    return await fn();
  } catch (error) {
    if (retries === 0) throw error;
    console.log(`Request failed, retrying... (${retries} attempts left)`);
    await new Promise(resolve => setTimeout(resolve, delay));
    return retryRequest(fn, retries - 1, delay * 1.5);
  }
};

// Train AI model with user data
export const trainAIModel = async (data) => {
  try {
    console.log('Training AI model with data:', data);
    
    // Use mock data in development mode if configured
    if (shouldSkipApiCalls()) {
      console.log('Using mock data for AI training in development mode');
      return getMockApiResponse('train-model');
    }
    
    // Fix the API endpoint path
    const response = await retryRequest(() => api.post('/api/vertex-ai/train-model', data), 2, 2000);
    return response.data;
  } catch (error) {
    console.error('AI model training failed:', error);
    
    if (isDevelopment) {
      console.log('Falling back to mock data due to API error');
      return getMockApiResponse('train-model');
    }
    
    // Provide more helpful error message
    if (error.response) {
      throw new Error(`Training failed: ${error.response.data.message || error.response.statusText}`);
    } else if (error.request) {
      throw new Error('Network error: Could not connect to the server. Please check your internet connection.');
    } else {
      throw new Error(`Error: ${error.message}`);
    }
  }
};

// Test AI model with a question
export const testAIModel = async (question) => {
  try {
    // Fix the API endpoint path
    const response = await retryRequest(() => api.post('/api/vertex-ai/test', { question }), 1, 1000);
    return response.data;
  } catch (error) {
    console.error('AI model test failed:', error);
    
    if (error.response) {
      throw new Error(`Test failed: ${error.response.data.message || error.response.statusText}`);
    } else if (error.request) {
      throw new Error('Network error: Could not connect to the server.');
    } else {
      throw new Error(`Error: ${error.message}`);
    }
  }
};

// Extract business info from website
export const extractBusinessInfo = async (source, value) => {
  try {
    console.log('Extracting business info from:', source, value);
    
    // Use mock data if we're offline or in development mode with skip API calls
    if (!navigator.onLine || process.env.REACT_APP_SKIP_API_CALLS === 'true') {
      console.log('Using mock data for business info extraction (offline or development mode)');
      return await mockExtractBusinessInfo(source, value);
    }
    
    // Fix the API endpoint path
    const response = await retryRequest(() => api.post('/api/vertex-ai/extract-business-info', { 
      source, 
      value 
    }), 1, 1000);
    
    return response.data;
  } catch (error) {
    console.error('Business info extraction error:', error);
    
    // Fall back to mock data if API call fails
    console.log('Falling back to mock data due to API error');
    return await mockExtractBusinessInfo(source, value);
  }
};
